import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { GroundworkMobileMenu } from "./GroundworkMobileMenu";

function GroundworkStyles() {
    return (
        <style>{`
            [data-web-template="groundwork-build"] .site-builder-section,
            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] {
                padding-inline: 1.25rem;
                position: relative;
            }

            [data-web-template="groundwork-build"] .site-builder-grid {
                display: grid;
                gap: clamp(1.2rem, 2.7vw, 2.6rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="groundwork-build"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="groundwork-build"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="groundwork-build"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.15rem;
                min-width: 0;
            }

            [data-web-template="groundwork-build"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="groundwork-build"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2vw, 1.9rem);
                justify-content: center;
            }

            [data-web-template="groundwork-build"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="groundwork-build"] .site-builder-navigation-link,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text::before,
            [data-web-template="groundwork-build"] .site-builder-navigation-link::before {
                background: var(--site-accent);
                content: "";
                height: 5px;
                left: 0;
                opacity: 0;
                position: absolute;
                top: calc(100% + .45rem);
                transform: translateX(-4px);
                transition: opacity 150ms ease, transform 150ms ease;
                width: 5px;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text:hover::before,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text:focus-visible::before,
            [data-web-template="groundwork-build"] .site-builder-navigation-link:hover::before,
            [data-web-template="groundwork-build"] .site-builder-navigation-link:focus-visible::before {
                opacity: 1;
                transform: translateX(0);
            }

            [data-web-template="groundwork-build"] [data-web-region$="Hero"] {
                background-image:
                    linear-gradient(color-mix(in srgb, var(--site-primary) 7%, transparent) 1px, transparent 1px),
                    linear-gradient(90deg, color-mix(in srgb, var(--site-primary) 7%, transparent) 1px, transparent 1px);
                background-position: center;
                background-size: clamp(52px, 6vw, 84px) clamp(52px, 6vw, 84px);
            }

            [data-web-template="groundwork-build"] [data-web-region="header"] {
                position: sticky;
                top: 0;
                z-index: 60;
            }

            [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerCta"],
            [data-web-template="groundwork-build"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroCopy"] {
                column-gap: .75rem;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 1.35rem;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroCopy"] > :not([data-document-role="heroPrimaryAction"], [data-document-role="heroSecondaryAction"]) {
                grid-column: 1 / -1;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroVisual"],
            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] {
                position: relative;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroVisual"]::before,
            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"]::before {
                border-left: 3px solid var(--site-accent);
                border-top: 3px solid var(--site-accent);
                content: "";
                height: 2.5rem;
                left: -.6rem;
                pointer-events: none;
                position: absolute;
                top: -.6rem;
                width: 2.5rem;
                z-index: 2;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroFloatingNote"] {
                bottom: 1.5rem;
                left: 1.5rem;
                position: absolute;
                z-index: 3;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid {
                gap: 0;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item {
                border-left: 1px solid var(--site-border);
                padding-inline: clamp(1rem, 2.4vw, 2rem);
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 1px solid var(--site-border);
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="groundwork-build"] .groundwork-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="groundwork-build"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="groundwork-build"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroCopy"] {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="groundwork-build"] [data-document-role="heroCopy"] > *,
                [data-web-template="groundwork-build"] [data-document-role="heroPrimaryAction"],
                [data-web-template="groundwork-build"] [data-document-role="heroSecondaryAction"] {
                    grid-column: 1 !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroVisual"],
                [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] {
                    min-height: min(70svh, 620px) !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroVisual"] .site-builder-image,
                [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] .site-builder-image {
                    min-height: min(70svh, 620px) !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid var(--site-border);
                    border-right: 0;
                    grid-column: auto !important;
                    grid-row: auto !important;
                    padding-block: 1.5rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] {
                    min-height: 72px;
                    padding-inline: 1rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] .groundwork-mobile-menu-toggle {
                    align-items: center;
                    background: var(--site-primary);
                    border: 0;
                    border-radius: 3px;
                    cursor: pointer;
                    display: grid;
                    gap: 7px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="groundwork-build"] .groundwork-mobile-menu-toggle > span {
                    background: var(--site-surface);
                    display: block;
                    height: 2px;
                    transition: transform 160ms ease;
                    width: 23px;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] {
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2rem, 9vw, 3.2rem) !important;
                    font-weight: 730 !important;
                    letter-spacing: -.04em !important;
                    width: 100% !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .groundwork-mobile-menu-toggle > span:first-child {
                    transform: translateY(4.5px) rotate(45deg);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .groundwork-mobile-menu-toggle > span:last-child {
                    transform: translateY(-4.5px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="groundwork-build"] .site-builder-section,
                [data-web-template="groundwork-build"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroVisual"],
                [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] {
                    min-height: 420px !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroVisual"] .site-builder-image,
                [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] .site-builder-image {
                    min-height: 420px !important;
                }

                [data-web-template="groundwork-build"] .site-builder-column {
                    grid-template-columns: minmax(0, 1fr) !important;
                }
            }

            /* Groundwork industrial blueprint */
            [data-web-template="groundwork-build"] {
                --groundwork-ease: cubic-bezier(.2, .7, .2, 1);
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 102px;
            }

            [data-web-template="groundwork-build"] ::selection {
                background: var(--site-accent);
                color: var(--site-text);
            }

            [data-web-template="groundwork-build"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="groundwork-build"] *,
            [data-web-template="groundwork-build"] *::before,
            [data-web-template="groundwork-build"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="groundwork-build"] .site-builder-section,
            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] {
                padding-inline: clamp(1.25rem, 2.6vw, 2.5rem);
            }

            [data-web-template="groundwork-build"] .site-builder-text-h1,
            [data-web-template="groundwork-build"] .site-builder-text-h2,
            [data-web-template="groundwork-build"] .site-builder-text-h3 {
                font-stretch: condensed;
                text-wrap: balance;
            }

            [data-web-template="groundwork-build"] .site-builder-text-h1 {
                line-height: .86 !important;
                text-transform: uppercase;
            }

            [data-web-template="groundwork-build"] .site-builder-text-h2 {
                line-height: .93 !important;
            }

            [data-web-template="groundwork-build"] .site-builder-button {
                align-items: center;
                border-radius: 0 !important;
                letter-spacing: .095em;
                min-height: 52px;
                text-transform: uppercase;
                transition:
                    background-color 180ms ease,
                    border-color 180ms ease,
                    color 180ms ease,
                    transform 180ms ease;
            }

            [data-web-template="groundwork-build"] .site-builder-button:hover {
                transform: translateX(4px);
            }

            [data-web-template="groundwork-build"] .site-builder-card {
                border-radius: 0 !important;
                transition:
                    border-color 240ms ease,
                    box-shadow 240ms ease,
                    transform 240ms var(--groundwork-ease);
            }

            [data-web-template="groundwork-build"] .site-builder-card:hover {
                border-color: color-mix(in srgb, var(--site-accent) 68%, var(--site-border)) !important;
                box-shadow: 0 22px 52px rgb(23 26 26 / .1);
                transform: translateY(-4px);
            }

            [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] {
                left: 0;
                padding-inline: clamp(1rem, 2.6vw, 2.5rem);
                position: sticky;
                right: 0;
                top: 0;
                z-index: 64;
            }

            [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid {
                align-items: center;
                gap: 1rem;
            }

            [data-web-template="groundwork-build"] [data-web-region="header"] {
                left: 0;
                padding-inline: clamp(1rem, 2.6vw, 2.5rem);
                position: sticky;
                right: 0;
                top: 34px;
                transition:
                    background-color 220ms ease,
                    box-shadow 220ms ease,
                    min-height 220ms ease;
                z-index: 63;
            }

            [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
                gap: clamp(.7rem, 1.3vw, 1.25rem);
            }

            [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-header-scrolled] {
                background: color-mix(in srgb, var(--site-surface) 98%, transparent) !important;
                box-shadow: 0 12px 34px rgb(23 26 26 / .14);
                min-height: 58px !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="brand"] {
                clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 50%, calc(100% - 22px) 100%, 0 100%);
                margin-left: clamp(-2.5rem, -2.6vw, -1rem);
                padding-right: clamp(2.5rem, 3.8vw, 4rem) !important;
                transition: min-height 220ms ease;
            }

            [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-header-scrolled] [data-document-role="brand"] {
                min-height: 58px !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] {
                gap: clamp(.3rem, .7vw, .65rem) !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-root {
                width: 100%;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline {
                border: 0;
                background: transparent;
                justify-content: center;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline > .site-navigation-primary-item + li {
                border-left: 0;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline .site-navigation-primary-trigger,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline .site-navigation-primary-link {
                min-height: 44px;
                padding-inline: clamp(.35rem, .7vw, .65rem);
            }

            [data-web-template="groundwork-build"] .site-builder-navigation-link,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] .site-builder-text {
                font-size: .7rem !important;
                font-weight: 820 !important;
                letter-spacing: .08em !important;
                text-transform: uppercase;
            }

            [data-web-template="groundwork-build"] .site-builder-navigation-link::before,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text::before {
                bottom: -.1rem;
                height: 2px;
                left: 0;
                top: auto;
                transform: scaleX(0);
                transform-origin: right;
                transition: transform 180ms ease;
                width: 100%;
            }

            [data-web-template="groundwork-build"] .site-builder-navigation-link:hover::before,
            [data-web-template="groundwork-build"] .site-builder-navigation-link:focus-visible::before,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text:hover::before,
            [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-builder-text:focus-visible::before {
                transform: scaleX(1);
                transform-origin: left;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="groundwork-build"] [data-document-role="headerCta"] {
                min-width: 0 !important;
                padding-inline: clamp(.75rem, 1.2vw, 1rem) !important;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] {
                overflow: hidden;
                padding-inline: 0;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid {
                gap: 0 !important;
                grid-auto-rows: minmax(0, auto) !important;
                grid-template-columns: minmax(0, .94fr) minmax(520px, 1.06fr) !important;
                max-width: none;
                width: 100%;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 !important;
                grid-row: 1 !important;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                grid-column: 2 !important;
                grid-row: 1 !important;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroCopy"] {
                align-content: center;
                column-gap: .75rem;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                padding-left: max(1.5rem, calc((100cqw - var(--site-container)) / 2)) !important;
                row-gap: 1.3rem;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroCopy"] > :not([data-document-role="heroPrimaryAction"], [data-document-role="heroSecondaryAction"]) {
                grid-column: 1 / -1;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"] {
                clip-path: polygon(7% 0, 100% 0, 100% 100%, 0 100%);
                isolation: isolate;
                margin-left: -3.5rem;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"]::before {
                background:
                    linear-gradient(90deg, rgb(23 26 26 / .42), transparent 48%),
                    linear-gradient(0deg, rgb(23 26 26 / .46), transparent 48%);
                border: 0;
                content: "";
                height: auto;
                inset: 0;
                pointer-events: none;
                position: absolute;
                width: auto;
                z-index: 1;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"]::after {
                background:
                    linear-gradient(90deg, color-mix(in srgb, var(--site-accent) 62%, transparent) 1px, transparent 1px),
                    linear-gradient(color-mix(in srgb, var(--site-accent) 62%, transparent) 1px, transparent 1px);
                background-size: 28px 28px;
                bottom: 0;
                clip-path: polygon(0 38%, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: 180px;
                left: 0;
                opacity: .52;
                pointer-events: none;
                position: absolute;
                width: 220px;
                z-index: 2;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroImage"] {
                inset: 0;
                position: absolute;
            }

            [data-web-template="groundwork-build"] [data-document-role="heroImage"] .site-builder-image {
                filter: grayscale(100%) contrast(1.08);
                height: 100% !important;
                max-width: none;
                object-fit: cover;
                transition: filter 650ms var(--groundwork-ease), transform 900ms var(--groundwork-ease);
                width: 100% !important;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"]:hover [data-document-role="heroImage"] .site-builder-image {
                filter: grayscale(42%) contrast(1.03);
                transform: scale(1.025);
            }

            [data-web-template="groundwork-build"] [data-document-role="heroFloatingNote"] {
                border-left: 4px solid var(--site-accent) !important;
                bottom: clamp(1.25rem, 3vw, 2.5rem);
                left: auto;
                max-width: calc(100% - 3rem);
                position: absolute;
                right: clamp(1.25rem, 3vw, 2.5rem);
                z-index: 4;
            }

            [data-web-template="groundwork-build"] [data-web-region$="Hero"]:not([data-web-region="homeHero"]) {
                border-bottom: 1px solid var(--site-border);
                overflow: hidden;
            }

            [data-web-template="groundwork-build"] [data-web-region$="Hero"]:not([data-web-region="homeHero"])::after {
                background: var(--site-accent);
                bottom: 0;
                clip-path: polygon(32% 0, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: 9px;
                pointer-events: none;
                position: absolute;
                right: 0;
                width: min(34vw, 420px);
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] {
                padding-inline: clamp(1.25rem, 2.6vw, 2.5rem);
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item {
                border-left: 1px solid var(--site-border);
                min-height: 190px;
                padding: clamp(1.25rem, 2.6vw, 2.25rem);
                transition: background-color 220ms ease;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item:hover {
                background: color-mix(in srgb, var(--site-accent) 7%, var(--site-surface));
            }

            [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 1px solid var(--site-border);
            }

            [data-web-template="groundwork-build"] [data-document-role="serviceCard"] {
                border-top: 4px solid var(--site-primary) !important;
                position: relative;
            }

            [data-web-template="groundwork-build"] [data-document-role="serviceCard"]::after {
                background: var(--site-accent);
                content: "";
                height: 4px;
                left: -1px;
                position: absolute;
                top: -4px;
                transition: width 260ms var(--groundwork-ease);
                width: 38px;
            }

            [data-web-template="groundwork-build"] [data-document-role="serviceCard"]:hover::after {
                width: calc(100% + 2px);
            }

            [data-web-template="groundwork-build"] [data-document-role="processStep"] {
                border-top-width: 3px !important;
                box-shadow: none !important;
                position: relative;
            }

            [data-web-template="groundwork-build"] [data-document-role="processStep"]::before {
                background: var(--site-accent);
                content: "";
                height: 3px;
                left: 0;
                position: absolute;
                top: -3px;
                width: clamp(36px, 24%, 72px);
            }

            [data-web-template="groundwork-build"] [data-document-role="processStep"]:hover {
                box-shadow: none;
            }

            [data-web-template="groundwork-build"] [data-document-role="roleCard"] {
                border-bottom: 4px solid var(--site-primary) !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="projectMeta"] {
                border-left: 4px solid var(--site-accent) !important;
                box-shadow: none !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] {
                clip-path: polygon(0 0, 100% 0, 100% calc(100% - 44px), calc(100% - 44px) 100%, 0 100%);
                isolation: isolate;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"] .site-builder-image {
                filter: grayscale(82%) contrast(1.06);
                height: 100% !important;
                object-fit: cover;
                transition: filter 650ms var(--groundwork-ease), transform 850ms var(--groundwork-ease);
                width: 100% !important;
            }

            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"]:hover .site-builder-image {
                filter: grayscale(0%) contrast(1);
                transform: scale(1.025);
            }

            [data-web-template="groundwork-build"] [data-document-role="editorialVisual"]::before {
                border-left: 4px solid var(--site-accent);
                border-top: 4px solid var(--site-accent);
                height: 3rem;
                left: 0;
                top: 0;
                width: 3rem;
            }

            [data-web-template="groundwork-build"] [data-web-region="homeFeaturedProject"],
            [data-web-template="groundwork-build"] [data-web-region="projectFamilyHouse"],
            [data-web-template="groundwork-build"] [data-web-region="projectInterior"],
            [data-web-template="groundwork-build"] [data-web-region="aboutTeamModel"],
            [data-web-template="groundwork-build"] [data-web-region="aboutQualitySystem"],
            [data-web-template="groundwork-build"] [data-web-region="contactForm"] {
                border-bottom: 1px solid color-mix(in srgb, var(--site-border) 68%, transparent);
                border-top: 1px solid color-mix(in srgb, var(--site-border) 68%, transparent);
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="accordion"] {
                border-top: 1px solid var(--site-border);
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="accordionItem"] {
                border-bottom: 1px solid var(--site-border);
            }

            [data-web-template="groundwork-build"] .site-builder-form {
                border-radius: 0 !important;
                box-shadow: 12px 12px 0 color-mix(in srgb, var(--site-accent) 72%, transparent);
            }

            [data-web-template="groundwork-build"] [data-web-region$="Cta"],
            [data-web-template="groundwork-build"] [data-web-region="homeContactForm"] {
                overflow: hidden;
            }

            [data-web-template="groundwork-build"] [data-web-region$="Cta"]::after,
            [data-web-template="groundwork-build"] [data-web-region="homeContactForm"]::after {
                background:
                    linear-gradient(90deg, color-mix(in srgb, var(--site-accent) 34%, transparent) 1px, transparent 1px),
                    linear-gradient(color-mix(in srgb, var(--site-accent) 34%, transparent) 1px, transparent 1px);
                background-size: 34px 34px;
                bottom: -5rem;
                clip-path: polygon(34% 0, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: 18rem;
                opacity: .42;
                pointer-events: none;
                position: absolute;
                right: -3rem;
                width: 24rem;
            }

            [data-web-template="groundwork-build"] [data-web-region$="Cta"] > .site-builder-grid,
            [data-web-template="groundwork-build"] [data-web-region="homeContactForm"] > .site-builder-grid {
                position: relative;
                z-index: 1;
            }

            [data-web-template="groundwork-build"] [data-web-region$="Cta"] .site-builder-button {
                width: 100% !important;
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] {
                isolation: isolate;
                overflow: hidden;
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"]::after {
                border: 1px solid color-mix(in srgb, var(--site-accent) 46%, transparent);
                content: "";
                height: 22rem;
                pointer-events: none;
                position: absolute;
                right: -12rem;
                top: -10rem;
                transform: rotate(35deg);
                width: 28rem;
                z-index: -1;
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .8rem;
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] a {
                transition: color 180ms ease, transform 180ms ease;
            }

            [data-web-template="groundwork-build"] [data-site-builder-block="footer"] a:hover {
                color: var(--site-accent) !important;
                transform: translateX(3px);
            }

            [data-web-template="groundwork-build"]:not([data-web-template-editor="true"])[data-groundwork-runtime-ready] [data-groundwork-reveal]:not([data-groundwork-revealed]) {
                opacity: 0;
                transform: translateY(28px);
            }

            [data-web-template="groundwork-build"]:not([data-web-template-editor="true"])[data-groundwork-runtime-ready] [data-groundwork-reveal] {
                opacity: 1;
                transform: translateY(0);
                transition:
                    opacity 720ms var(--groundwork-ease),
                    transform 720ms var(--groundwork-ease);
                will-change: opacity, transform;
            }

            @container (max-width: 1180px) and (min-width: 901px) {
                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid {
                    gap: .45rem;
                }

                [data-web-template="groundwork-build"] [data-document-role="brand"] {
                    padding-left: 1.5rem !important;
                    padding-right: 2.25rem !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline .site-navigation-primary-trigger,
                [data-web-template="groundwork-build"] [data-document-role="headerNavigation"] .site-navigation-primary-boldUnderline .site-navigation-primary-link {
                    font-size: .65rem;
                    letter-spacing: .035em;
                    padding-inline: .3rem;
                }

                [data-web-template="groundwork-build"] [data-document-role="headerCta"] {
                    font-size: .65rem !important;
                    padding-inline: .7rem !important;
                }
            }

            @container (max-width: 900px) {
                [data-web-template="groundwork-build"] {
                    scroll-padding-top: 96px;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] {
                    min-height: 30px !important;
                    padding-block: 6px !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) auto !important;
                    grid-template-rows: none !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: 1 !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) {
                    display: none;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] {
                    min-height: 64px !important;
                    padding-inline: 1rem;
                    top: 30px;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="brand"] {
                    margin-left: -1rem;
                    max-width: 255px;
                    min-height: 64px !important;
                    padding-left: 1rem !important;
                    padding-right: 2.5rem !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"] .groundwork-mobile-menu-toggle {
                    background: var(--site-surface);
                    border: 1px solid var(--site-border);
                    border-radius: 0;
                    color: var(--site-primary);
                    gap: 5px;
                    height: 44px;
                    top: 10px;
                    width: 44px;
                }

                [data-web-template="groundwork-build"] .groundwork-mobile-menu-toggle > span {
                    background: currentColor;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] {
                    background: var(--site-surface) !important;
                    min-height: 100svh !important;
                    padding-inline: 1rem;
                    top: 0;
                    z-index: 82;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="brand"] {
                    min-height: 64px !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    margin-top: clamp(2rem, 7vh, 4rem);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] [data-document-role="headerNavigation"] {
                    width: 100%;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .site-navigation-mobile-tree {
                    width: 100%;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .site-navigation-mobile-boldUnderline > .site-navigation-mobile-item {
                    border-color: var(--site-border);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .site-navigation-mobile-boldUnderline > .site-navigation-mobile-item > .site-navigation-mobile-link,
                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .site-navigation-mobile-boldUnderline > .site-navigation-mobile-item > .site-navigation-mobile-trigger,
                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .site-navigation-mobile-boldUnderline > .site-navigation-mobile-item > .site-navigation-mobile-parent {
                    min-height: 62px;
                    font-size: clamp(1rem, 4.5vw, 1.3rem);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .groundwork-mobile-menu-toggle > span:first-child {
                    transform: translateY(7px) rotate(45deg);
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .groundwork-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="groundwork-build"] [data-web-region="header"][data-groundwork-mobile-menu-open] .groundwork-mobile-menu-toggle > span:last-child {
                    transform: translateY(-7px) rotate(-45deg);
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="groundwork-build"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroCopy"] {
                    padding: clamp(3.75rem, 10vw, 6rem) 1.25rem !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"] {
                    clip-path: none;
                    margin-left: 0;
                    min-height: min(68svh, 620px) !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item {
                    min-height: 170px;
                }

                [data-web-template="groundwork-build"] [data-web-region$="Cta"] .site-builder-grid-item,
                [data-web-template="groundwork-build"] [data-web-region="homeContactForm"] .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }
            }

            @container (max-width: 640px) {
                [data-web-template="groundwork-build"] .site-builder-section,
                [data-web-template="groundwork-build"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid > .site-builder-grid-item:last-child {
                    display: none;
                }

                [data-web-template="groundwork-build"] [data-web-region="technicalStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroCopy"] {
                    grid-template-columns: minmax(0, 1fr);
                    padding: 3.5rem 1rem !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroCopy"] > *,
                [data-web-template="groundwork-build"] [data-document-role="heroPrimaryAction"],
                [data-web-template="groundwork-build"] [data-document-role="heroSecondaryAction"] {
                    grid-column: 1 !important;
                    width: 100% !important;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeHero"] [data-document-role="heroVisual"] {
                    min-height: 460px !important;
                }

                [data-web-template="groundwork-build"] [data-document-role="heroFloatingNote"] {
                    bottom: 1rem;
                    left: 1rem;
                    max-width: none;
                    right: 1rem;
                }

                [data-web-template="groundwork-build"] [data-web-region="homeSpecs"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid var(--site-border);
                    border-left: 1px solid var(--site-border);
                    border-right: 1px solid var(--site-border);
                    min-height: 0;
                    padding: 1.4rem;
                }

                [data-web-template="groundwork-build"] .site-builder-form {
                    box-shadow: 7px 7px 0 color-mix(in srgb, var(--site-accent) 72%, transparent);
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="groundwork-build"] {
                    scroll-behavior: auto !important;
                }

                [data-web-template="groundwork-build"] *,
                [data-web-template="groundwork-build"] *::before,
                [data-web-template="groundwork-build"] *::after {
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="groundwork-build"] [data-groundwork-reveal] {
                    opacity: 1 !important;
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function GroundworkFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-[3px] [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-bold [&_h1]:leading-[.92] [&_h1]:tracking-[-.045em] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-bold [&_h2]:tracking-[-.035em] [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const groundworkBuildTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "groundwork-build",
        name: "Stavební firma",
        type: "website",
        category: "construction",
        description: "Moderní technická šablona pro stavební firmy, rekonstrukce a architektonicky vedené realizace.",
        previewImage: "/templates/groundwork-build/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["groundwork-build"],
        pageTypes: {
            home: {
                label: "Construction homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Construction content page",
                slots: ["richContent"],
            },
            contact: {
                label: "Construction contact page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            groundworkBuildTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="groundwork-build"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <GroundworkStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <GroundworkMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <GroundworkFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
